import * as React from "react";
import { Link, useTranslation } from "gatsby-plugin-react-i18next";
import LanguageSwitcher from "./LanguageSwitcher";
import Logo from "./Logo";

type HeaderProps = {
  pathname?: string;
};

export default function Header({ pathname }: HeaderProps) {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = React.useState(false);

  React.useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  React.useEffect(() => {
    if (typeof document === "undefined") return;
    if (!menuOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [menuOpen]);

  return (
    <header className="site-header">
      {menuOpen ? (
        <div
          className="nav-backdrop"
          role="presentation"
          aria-hidden="true"
          onClick={() => setMenuOpen(false)}
        />
      ) : null}
      <div className="container header-inner">
        <Link to="/" className="header-brand">
          <Logo />
          <div className="brand-text">
            <span className="brand-name">{t("brand.name")}</span>
            <span className="brand-tagline">{t("brand.tagline")}</span>
          </div>
        </Link>

        <div className="header-right">
          <div className="header-controls">
            <button
              type="button"
              className="pill nav-toggle"
              aria-expanded={menuOpen}
              aria-controls="primary-nav"
              onClick={() => setMenuOpen((prev) => !prev)}
            >
              {menuOpen ? t("nav.close") : t("nav.menu")}
            </button>
            <LanguageSwitcher />
            <Link to="/circle-of-ihsan" className="btn secondary header-cta" style={{ boxShadow: "none" }}>
              {t("cta.join")}
            </Link>
          </div>

          <nav
            id="primary-nav"
            aria-label={t("a11y.primaryNav")}
            className={`header-nav${menuOpen ? " open" : ""}`}
          >
            <div className="nav-items">
              <Link to="/" className="pill">
                {t("nav.home")}
              </Link>
              <Link to="/about" className="pill">
                {t("nav.about")}
              </Link>
              <Link to="/programs" className="pill">
                {t("nav.programs")}
              </Link>
              <Link to="/services" className="pill">
                {t("nav.services")}
              </Link>
              <Link to="/events" className="pill">
                {t("nav.events")}
              </Link>
              <Link to="/blog" className="pill">
                {t("nav.blog")}
              </Link>
              <Link to="/contact" className="pill">
                {t("nav.contact")}
              </Link>
            </div>

            <div className="nav-actions">
              <Link to="/circle-of-ihsan" className="btn secondary nav-cta" style={{ boxShadow: "none" }}>
                {t("cta.join")}
              </Link>
            </div>
          </nav>
        </div>
      </div>
    </header>
  );
}
